import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Send, Paperclip, Bold, Italic, Underline, 
  List, ListOrdered, Link2, Image, 
  Loader2, Sparkles, Wand2, Copy, Check,
  ChevronDown, RotateCcw, Zap
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { auth } from '../../utils/auth';
import { cn } from '../../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface EmailComposeModalProps {
  isOpen: boolean;
  onClose: () => void;
  replyTo?: any;
  mode?: 'compose' | 'reply' | 'replyAll';
  onSent?: () => void;
}

const EmailComposeModal: React.FC<EmailComposeModalProps> = ({ 
  isOpen, 
  onClose, 
  replyTo = null,
  mode = 'compose',
  onSent
}) => {
  // Form state
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  
  // UI state
  const [sending, setSending] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [aiTone, setAiTone] = useState('professional');
  const [showToneSelector, setShowToneSelector] = useState(false);
  const [aiDraft, setAiDraft] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  
  // Pre-fill for reply mode
  useEffect(() => {
    if (replyTo && (mode === 'reply' || mode === 'replyAll')) {
      const fromEmail = replyTo.from?.emailAddress?.address || replyTo.from?.email || '';
      setTo(fromEmail);
      setSubject(`Re: ${replyTo.subject || ''}`);
      
      if (mode === 'replyAll' && replyTo.toRecipients) {
        const ccEmails = replyTo.toRecipients
          .map((r: any) => r.emailAddress?.address || r.email)
          .filter((e: string) => e)
          .join(', ');
        setCc(ccEmails);
        setShowCc(true);
      }
    }
  }, [replyTo, mode]);
  
  // Generate AI reply
  const handleGenerateAIReply = async () => {
    if (!replyTo?.id) {
      toast.error('Kan ikke generere svar uten original melding');
      return;
    }
    
    setGeneratingAI(true);
    setAiDraft(null);
    
    try {
      const token = auth.getToken();
      const response = await axios.post(
        `${API}/outlook/ai/generate-reply`,
        null,
        {
          params: {
            message_id: replyTo.id,
            tone: aiTone,
            include_questions: true
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data?.draft) {
        setAiDraft(response.data.draft);
        toast.success('AI-utkast generert!');
      }
    } catch (error) {
      console.error('AI generation error:', error);
      toast.error('Kunne ikke generere AI-svar');
    } finally {
      setGeneratingAI(false);
    }
  };
  
  // Use AI draft
  const handleUseAIDraft = () => {
    if (aiDraft) {
      setBody(aiDraft);
      setAiDraft(null);
      toast.success('AI-utkast lagt til');
    }
  };
  
  // Copy AI draft
  const handleCopyDraft = async () => {
    if (aiDraft) {
      await navigator.clipboard.writeText(aiDraft);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Kopiert til utklippstavle');
    }
  };
  
  // Send email
  const handleSend = async () => {
    if (!to.trim()) {
      toast.error('Vennligst oppgi mottaker');
      return;
    }
    
    if (!body.trim()) {
      toast.error('E-posten kan ikke være tom');
      return;
    }
    
    setSending(true);
    try {
      const token = auth.getToken();
      
      if (mode === 'reply' || mode === 'replyAll') {
        await axios.post(
          `${API}/outlook/reply`,
          {
            message_id: replyTo.id,
            body: body,
            is_html: true,
            reply_all: mode === 'replyAll'
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        const toRecipients = to.split(',').map(e => ({ 
          email: e.trim(),
          name: e.trim()
        }));
        
        const ccRecipients = cc ? cc.split(',').map(e => ({ 
          email: e.trim(),
          name: e.trim()
        })) : [];
        
        const bccRecipients = bcc ? bcc.split(',').map(e => ({ 
          email: e.trim(),
          name: e.trim()
        })) : [];
        
        await axios.post(
          `${API}/outlook/send`,
          {
            to: toRecipients,
            cc: ccRecipients.length > 0 ? ccRecipients : undefined,
            bcc: bccRecipients.length > 0 ? bccRecipients : undefined,
            subject: subject,
            body: body,
            is_html: true
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      toast.success('E-post sendt!');
      onSent?.();
      onClose();
      
    } catch (error: any) {
      console.error('Send error:', error);
      toast.error(error.response?.data?.detail || 'Kunne ikke sende e-post');
    } finally {
      setSending(false);
    }
  };
  
  const toneOptions = [
    { value: 'professional', label: 'Profesjonell', icon: '💼', desc: 'Formell forretningstone' },
    { value: 'short', label: 'Kortfattet', icon: '⚡', desc: 'Rett på sak' },
    { value: 'formal', label: 'Formell', icon: '📜', desc: 'Juridisk presis' },
    { value: 'friendly', label: 'Vennlig', icon: '😊', desc: 'Avslappet og personlig' }
  ];
  
  const selectedTone = toneOptions.find(t => t.value === aiTone) || toneOptions[0];
  
  if (!isOpen) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        {/* Backdrop */}
        <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm" />
        
        {/* Modal */}
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          transition={{ type: "spring", damping: 30, stiffness: 400 }}
          className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
        >
          {/* Header - Minimalist Lexy Style */}
          <div className="relative px-6 py-5 border-b border-slate-100">
            {/* Subtle gradient accent */}
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-violet-500 via-purple-500 to-indigo-500" />
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 bg-gradient-to-br from-violet-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-violet-500/20">
                  <Send className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">
                    {mode === 'reply' ? 'Svar på e-post' : mode === 'replyAll' ? 'Svar alle' : 'Ny e-post'}
                  </h2>
                  {replyTo && (
                    <p className="text-sm text-slate-500 mt-0.5 truncate max-w-md">
                      {replyTo.subject}
                    </p>
                  )}
                </div>
              </div>
              
              <button
                onClick={onClose}
                className="p-2.5 hover:bg-slate-100 rounded-xl transition-all duration-200 group"
              >
                <X className="w-5 h-5 text-slate-400 group-hover:text-slate-600" />
              </button>
            </div>
          </div>
          
          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {/* Recipients Section */}
            <div className="px-6 py-4 space-y-3 bg-slate-50/50">
              {/* To */}
              <div className="flex items-center gap-4">
                <label className="text-sm font-medium text-slate-500 w-16">Til</label>
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={to}
                    onChange={(e) => setTo(e.target.value)}
                    placeholder="mottaker@eksempel.no"
                    className="w-full text-sm text-slate-900 bg-white border border-slate-200 rounded-lg px-4 py-2.5 outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100 transition-all"
                  />
                </div>
                <div className="flex items-center gap-2">
                  {!showCc && (
                    <button
                      onClick={() => setShowCc(true)}
                      className="text-xs font-medium text-violet-600 hover:text-violet-700 px-2 py-1 hover:bg-violet-50 rounded-md transition-colors"
                    >
                      + Kopi
                    </button>
                  )}
                  {!showBcc && (
                    <button
                      onClick={() => setShowBcc(true)}
                      className="text-xs font-medium text-violet-600 hover:text-violet-700 px-2 py-1 hover:bg-violet-50 rounded-md transition-colors"
                    >
                      + Blindkopi
                    </button>
                  )}
                </div>
              </div>
              
              {/* CC */}
              <AnimatePresence>
                {showCc && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="flex items-center gap-4"
                  >
                    <label className="text-sm font-medium text-slate-500 w-16">Kopi</label>
                    <input
                      type="text"
                      value={cc}
                      onChange={(e) => setCc(e.target.value)}
                      placeholder="kopi@eksempel.no"
                      className="flex-1 text-sm text-slate-900 bg-white border border-slate-200 rounded-lg px-4 py-2.5 outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100 transition-all"
                    />
                  </motion.div>
                )}
              </AnimatePresence>
              
              {/* BCC */}
              <AnimatePresence>
                {showBcc && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="flex items-center gap-4"
                  >
                    <label className="text-sm font-medium text-slate-500 w-16">Blind</label>
                    <input
                      type="text"
                      value={bcc}
                      onChange={(e) => setBcc(e.target.value)}
                      placeholder="blindkopi@eksempel.no"
                      className="flex-1 text-sm text-slate-900 bg-white border border-slate-200 rounded-lg px-4 py-2.5 outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100 transition-all"
                    />
                  </motion.div>
                )}
              </AnimatePresence>
              
              {/* Subject */}
              <div className="flex items-center gap-4">
                <label className="text-sm font-medium text-slate-500 w-16">Emne</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Skriv emne..."
                  className="flex-1 text-sm font-medium text-slate-900 bg-white border border-slate-200 rounded-lg px-4 py-2.5 outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100 transition-all"
                />
              </div>
            </div>
            
            {/* AI Assistant Section - World Class Design */}
            {(mode === 'reply' || mode === 'replyAll') && (
              <div className="px-6 py-4 border-b border-slate-100">
                <div className="bg-gradient-to-br from-violet-50 via-purple-50 to-indigo-50 rounded-2xl p-5 border border-violet-100/50">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-violet-500/25">
                        <Sparkles className="w-4.5 h-4.5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-slate-900">AI Skriveassistent</h3>
                        <p className="text-xs text-slate-500">Generer profesjonelt svar med ett klikk</p>
                      </div>
                    </div>
                    
                    {/* Tone Selector */}
                    <div className="relative">
                      <button
                        onClick={() => setShowToneSelector(!showToneSelector)}
                        className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 hover:border-violet-300 transition-all shadow-sm"
                      >
                        <span>{selectedTone.icon}</span>
                        <span>{selectedTone.label}</span>
                        <ChevronDown className={cn(
                          "w-4 h-4 text-slate-400 transition-transform",
                          showToneSelector && "rotate-180"
                        )} />
                      </button>
                      
                      <AnimatePresence>
                        {showToneSelector && (
                          <motion.div
                            initial={{ opacity: 0, y: -10, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -10, scale: 0.95 }}
                            className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-10"
                          >
                            {toneOptions.map((tone) => (
                              <button
                                key={tone.value}
                                onClick={() => {
                                  setAiTone(tone.value);
                                  setShowToneSelector(false);
                                }}
                                className={cn(
                                  "w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-slate-50 transition-colors",
                                  aiTone === tone.value && "bg-violet-50"
                                )}
                              >
                                <span className="text-lg">{tone.icon}</span>
                                <div>
                                  <p className={cn(
                                    "text-sm font-medium",
                                    aiTone === tone.value ? "text-violet-700" : "text-slate-700"
                                  )}>
                                    {tone.label}
                                  </p>
                                  <p className="text-xs text-slate-500">{tone.desc}</p>
                                </div>
                                {aiTone === tone.value && (
                                  <Check className="w-4 h-4 text-violet-600 ml-auto" />
                                )}
                              </button>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                  
                  {/* Generate Button */}
                  <Button
                    onClick={handleGenerateAIReply}
                    disabled={generatingAI}
                    className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white font-medium py-3 rounded-xl shadow-lg shadow-violet-500/25 transition-all duration-200"
                  >
                    {generatingAI ? (
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Genererer intelligent svar...</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Wand2 className="w-4 h-4" />
                        <span>Generer AI-svar</span>
                        <Zap className="w-3.5 h-3.5 ml-1 text-yellow-300" />
                      </div>
                    )}
                  </Button>
                  
                  {/* AI Draft Preview - World Class Formatting */}
                  <AnimatePresence>
                    {aiDraft && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="mt-4"
                      >
                        <div className="bg-white rounded-xl border border-violet-200 overflow-hidden shadow-sm">
                          {/* Draft Header */}
                          <div className="px-4 py-3 bg-gradient-to-r from-violet-500 to-purple-500 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Sparkles className="w-4 h-4 text-white" />
                              <span className="text-sm font-medium text-white">AI-generert utkast</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={handleCopyDraft}
                                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                                title="Kopier"
                              >
                                {copied ? (
                                  <Check className="w-4 h-4 text-green-300" />
                                ) : (
                                  <Copy className="w-4 h-4 text-white/80" />
                                )}
                              </button>
                              <button
                                onClick={handleGenerateAIReply}
                                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                                title="Regenerer"
                              >
                                <RotateCcw className="w-4 h-4 text-white/80" />
                              </button>
                            </div>
                          </div>
                          
                          {/* Draft Content - Beautiful Typography */}
                          <div className="p-5">
                            <div className="prose prose-sm max-w-none text-slate-700 leading-relaxed whitespace-pre-wrap font-normal">
                              {aiDraft.split('\n').map((line, i) => {
                                // Style different parts of the email
                                if (line.startsWith('Hei') || line.startsWith('Kjære')) {
                                  return <p key={i} className="font-medium text-slate-900 mb-3">{line}</p>;
                                }
                                if (line.startsWith('Med vennlig hilsen') || line.startsWith('Beste hilsen') || line.startsWith('Mvh')) {
                                  return <p key={i} className="font-medium text-slate-800 mt-4 mb-1">{line}</p>;
                                }
                                if (line.trim() === '') {
                                  return <br key={i} />;
                                }
                                return <p key={i} className="mb-2">{line}</p>;
                              })}
                            </div>
                          </div>
                          
                          {/* Draft Actions */}
                          <div className="px-4 py-3 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setAiDraft(null)}
                              className="text-slate-600"
                            >
                              Forkast
                            </Button>
                            <Button
                              size="sm"
                              onClick={handleUseAIDraft}
                              className="bg-violet-600 hover:bg-violet-700 text-white gap-1.5"
                            >
                              <Check className="w-3.5 h-3.5" />
                              Bruk dette utkastet
                            </Button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            )}
            
            {/* Formatting Toolbar - Minimalist */}
            <div className="px-6 py-3 border-b border-slate-100 flex items-center gap-1 bg-slate-50/30">
              {[
                { icon: Bold, title: 'Fet' },
                { icon: Italic, title: 'Kursiv' },
                { icon: Underline, title: 'Understreket' },
              ].map((item, i) => (
                <button 
                  key={i}
                  type="button" 
                  title={item.title}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors group"
                >
                  <item.icon className="w-4 h-4 text-slate-500 group-hover:text-slate-700" />
                </button>
              ))}
              
              <div className="w-px h-5 bg-slate-200 mx-2" />
              
              {[
                { icon: List, title: 'Punktliste' },
                { icon: ListOrdered, title: 'Nummerert liste' },
              ].map((item, i) => (
                <button 
                  key={i}
                  type="button" 
                  title={item.title}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors group"
                >
                  <item.icon className="w-4 h-4 text-slate-500 group-hover:text-slate-700" />
                </button>
              ))}
              
              <div className="w-px h-5 bg-slate-200 mx-2" />
              
              {[
                { icon: Link2, title: 'Lenke' },
                { icon: Image, title: 'Bilde' },
              ].map((item, i) => (
                <button 
                  key={i}
                  type="button" 
                  title={item.title}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors group"
                >
                  <item.icon className="w-4 h-4 text-slate-500 group-hover:text-slate-700" />
                </button>
              ))}
            </div>
            
            {/* Body Editor */}
            <div className="px-6 py-5">
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Skriv din melding her..."
                className="w-full min-h-[280px] text-sm text-slate-800 outline-none resize-none leading-relaxed placeholder:text-slate-400"
              />
            </div>
            
            {/* Original Message Preview */}
            {replyTo && (
              <div className="px-6 py-4 bg-slate-50 border-t border-slate-100">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-1 h-4 bg-slate-300 rounded-full" />
                  <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Original melding</span>
                </div>
                <div className="pl-3 border-l-2 border-slate-200">
                  <div className="space-y-1 text-xs text-slate-600">
                    <p><span className="font-medium text-slate-700">Fra:</span> {replyTo.from?.emailAddress?.name || replyTo.from?.name}</p>
                    <p><span className="font-medium text-slate-700">Dato:</span> {new Date(replyTo.receivedDateTime || replyTo.received).toLocaleString('nb-NO', { 
                      day: 'numeric', 
                      month: 'long', 
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}</p>
                    <p><span className="font-medium text-slate-700">Emne:</span> {replyTo.subject}</p>
                  </div>
                  <div className="mt-3 text-xs text-slate-600 max-h-24 overflow-y-auto leading-relaxed">
                    {replyTo.bodyPreview || replyTo.body_preview}
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Footer - Clean & Minimal */}
          <div className="px-6 py-4 border-t border-slate-100 bg-white flex items-center justify-between">
            <button 
              type="button" 
              className="flex items-center gap-2 px-3 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors group"
            >
              <Paperclip className="w-4 h-4 text-slate-500 group-hover:text-violet-600" />
              <span className="text-sm">Vedlegg</span>
            </button>
            
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={onClose}
                disabled={sending}
                className="px-5"
              >
                Avbryt
              </Button>
              <Button
                onClick={handleSend}
                disabled={sending || !to.trim()}
                className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white gap-2 px-6 shadow-lg shadow-violet-500/25 transition-all duration-200"
              >
                {sending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sender...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send e-post
                  </>
                )}
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default EmailComposeModal;
